<template>
  <q-dialog v-model="dialogModel" transition-show="scale" transition-hide="scale" seamless no-backdrop-dismiss class="no-shadow-dialog">
    <q-card class="upload-dialog-card">
      <q-card-section class="upload-dialog-header">
        <div class="header-content">
          <div class="upload-dialog-title">Upload Content</div>
          <div class="upload-dialog-subtitle">
            <template v-if="uploadSource === 'drag-drop' && selectedFiles.length > 0"> {{ selectedFiles.length }} file{{ selectedFiles.length !== 1 ? 's' : '' }} ready to upload </template>
            <template v-else> Select content to upload to your gallery </template>
          </div>
        </div>
        <q-space />
        <q-btn icon="close" flat round dense v-close-popup class="close-btn" />
      </q-card-section>

      <q-separator />

      <q-card-section class="upload-dialog-body scroll-container">
        <!-- Upload area -->
        <UploadArea :files="selectedFiles" @add-files="handleAddFiles" @remove-file="removeFile" />

        <!-- Collection selection -->
        <CollectionSelector
          :destination="uploadDestination"
          :selected-collection="selectedCollection"
          :new-collection-name="newCollectionName"
          :collections="collections"
          :collectionsOptions="collectionsOptions"
          :isLoadingCollections="isLoadingCollections"
          @update:destination="handleDestinationUpdate"
          @update:selectedCollection="selectedCollection = $event"
          @update:newCollectionName="newCollectionName = $event"
        />
      </q-card-section>

      <q-card-actions align="right" class="upload-dialog-actions">
        <q-btn flat no-caps label="Cancel" color="grey-7" v-close-popup class="cancel-btn" />
        <q-btn flat no-caps :disabled="!canUpload" label="Upload" @click="handleUploadClick" class="upload-btn" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import { ref, computed, watch, getCurrentInstance } from '@vue/composition-api';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { ContentApi } from '@api/index';
import { CollectionsApi } from '@api/modules/collections';
import UploadArea from './upload/UploadArea.vue';
import CollectionSelector from './upload/CollectionSelector.vue';
import { checkStorageLimit } from '@/utils/resourceMetrics';
import fileUpload from '@/utils/fileUpload';

export default {
  name: 'UploadImagesDialog',
  components: {
    UploadArea,
    CollectionSelector
  },
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:show', 'upload-success', 'upload-error'],
  setup(props, { emit }) {
    const vm = getCurrentInstance();
    const store = vm.proxy.$store;

    // Dialog state
    const dialogModel = computed({
      get: () => props.show,
      set: value => emit('update:show', value)
    });

    // File state
    const selectedFiles = ref([]);

    // Upload state - only keep what's needed for collection handling
    const uploadedContentTypes = ref({});

    // Global upload progress tracking
    const currentGlobalUploads = ref([]);

    // Collection state
    const uploadDestination = ref('all');
    const selectedCollection = ref(null);
    const newCollectionName = ref('');

    // Get query client for cache invalidation
    const queryClient = useQueryClient();

    // Collections data - lazy loading for better performance
    const collectionsQueryEnabled = computed(() => {
      // Only fetch when dialog is open AND user might need collections
      return props.show && (uploadDestination.value === 'existing' || uploadDestination.value === 'new');
    });

    const { data: collectionsData, isLoading: isLoadingCollections } = useQuery({
      queryKey: ['collections'],
      queryFn: fetchCollections,
      staleTime: 1000 * 60 * 5, // 5 minutes
      enabled: collectionsQueryEnabled, // Only run when actually needed
      keepPreviousData: true // Keep previous data while loading new data
    });

    async function fetchCollections() {
      const response = await CollectionsApi.getAllCollections();
      return response.data.collections || [];
    }

    const collections = computed(() => collectionsData.value || []);

    // Memoized collections options to prevent unnecessary recalculations
    const collectionsOptions = computed(() => {
      if (!collections.value.length) return [];

      return collections.value.map(collection => ({
        label: collection.name,
        value: collection.id,
        description: `${collection.item_count} items`
      }));
    });

    // Optimized duplicate collection name checking with memoization
    const collectionNamesSet = computed(() => {
      if (!collections.value.length) return new Set();

      return new Set(collections.value.map(collection => collection.name.trim().toLowerCase()));
    });

    const isDuplicateCollectionName = name => {
      if (!name || !collectionNamesSet.value.size) {
        return false;
      }

      const trimmedName = name.trim().toLowerCase();
      return collectionNamesSet.value.has(trimmedName);
    };

    // Watch for dropped files from the drag-upload store
    const droppedFiles = computed(() => store.getters['dragUpload/droppedFiles']);
    const uploadSource = computed(() => store.getters['dragUpload/uploadSource']);

    // Initialize files from dropped files when dialog opens - use store files directly
    watch(
      () => props.show,
      (isVisible, wasVisible) => {
        if (isVisible && !wasVisible && droppedFiles.value.length > 0) {
          // Use the already processed files directly from store (no conversion needed)
          selectedFiles.value = [...droppedFiles.value];
        }
      },
      { immediate: true }
    );

    // Simplified watcher for new files added while dialog is open - use store files directly
    watch(
      () => droppedFiles.value.length, // Only watch length, not deep changes
      (newLength, oldLength) => {
        // Only process if dialog is visible and length actually increased
        if (props.show && newLength > oldLength && newLength > selectedFiles.value.length) {
          // Use store files directly without any conversion processing
          selectedFiles.value = [...droppedFiles.value];
        }
      }
    );

    // File handling - optimized for performance
    const handleAddFiles = newFiles => {
      // Add files directly without spread operator for better performance
      selectedFiles.value = selectedFiles.value.concat(newFiles);
    };

    const removeFile = index => {
      // Clean up preview URL only if it exists and is a blob URL
      const fileToRemove = selectedFiles.value[index];
      if (fileToRemove && fileToRemove.preview && fileToRemove.preview.startsWith('blob:')) {
        URL.revokeObjectURL(fileToRemove.preview);
      }

      // Use more efficient array removal
      selectedFiles.value.splice(index, 1);
    };

    // Validation - optimized and simplified
    const isNewCollectionNameValid = computed(() => {
      if (uploadDestination.value !== 'new') return true;
      if (!newCollectionName.value.trim()) return false;

      // Use direct validation for immediate feedback, debounced version for heavy operations
      return !isDuplicateCollectionName(newCollectionName.value);
    });

    const canUpload = computed(() => {
      if (selectedFiles.value.length === 0) return false;

      if (uploadDestination.value === 'existing') {
        return selectedCollection.value !== null;
      } else if (uploadDestination.value === 'new') {
        return newCollectionName.value.trim() !== '' && isNewCollectionNameValid.value;
      }

      return true; // For 'all' destination
    });

    // Upload process
    const handleDestinationUpdate = newDestination => {
      uploadDestination.value = newDestination;

      // Reset related values when changing destination
      if (newDestination === 'all') {
        selectedCollection.value = null;
        newCollectionName.value = '';
      } else if (newDestination === 'existing') {
        newCollectionName.value = '';
      } else if (newDestination === 'new') {
        selectedCollection.value = null;
      }
    };

    // Get total size of selected files
    const getTotalUploadSize = () => selectedFiles.value.reduce((total, { file }) => total + (file?.size || 0), 0);

    const handleUploadClick = () => {
      if (uploadDestination.value === 'new' && !isNewCollectionNameValid.value) {
        // Show error notification
        emit('upload-error', 'A collection with this name already exists. Please choose a different name.');
        return;
      }

      // Check storage limits using our new utility
      const storageCheck = checkStorageLimit(getTotalUploadSize());
      if (!storageCheck.allowed) {
        // Show error notification with detailed storage information
        emit(
          'upload-error',
          `Cannot upload: Storage limit exceeded. 
          Upload size: ${storageCheck.uploadSize}
          Available: ${storageCheck.remainingStorage}.
          Please upgrade your plan to add more storage.`
        );
        return;
      }

      uploadContent();
    };

    const uploadContent = async () => {
      if (!canUpload.value) return;

      try {
        // Store upload parameters for background processing
        const uploadParams = getUploadParameters();

        // Extract file list BEFORE clearing form state
        const fileList = selectedFiles.value.map(file => file.file);

        // Add files to global upload progress tracking with minimal processing
        currentGlobalUploads.value = await store.dispatch('uploadProgress/addUploads', fileList);

        // Close the dialog immediately - no waiting for heavy processing
        dialogModel.value = false;

        // Continue upload process in background without blocking UI
        processUploadInBackground(uploadParams, fileList);
      } catch (error) {
        console.error('Upload initialization error:', error);
        emit('upload-error', error.message);
      }
    };

    // Background upload processing - non-blocking
    async function processUploadInBackground(uploadParams, fileList) {
      try {
        // Prepare file data with dimensions (moved to background)
        const fileDataArray = await prepareFileDataBackground(fileList);

        // Get presigned URLs for upload
        const uploadData = await getPresignedUrls(fileDataArray);

        // Upload files using presigned URLs
        await uploadFilesToStorage(fileDataArray, uploadData.presignedUrls);

        // Create content batch
        const contentIds = await createContent(uploadData.contentMetadata);

        // Update global progress with real content IDs
        await updateGlobalProgressWithRealIds(contentIds);

        // Handle collection operations
        const collectionResult = await handleCollectionOperations(uploadParams, contentIds);

        // Cache invalidation
        queryClient.invalidateQueries({ queryKey: ['content'] });
        queryClient.invalidateQueries({ queryKey: ['collections'] });

        // Notify success
        emit('upload-success', {
          count: uploadParams.uploadedCount,
          collectionName: collectionResult.collectionName
        });
      } catch (uploadError) {
        console.error('Background upload error:', uploadError);

        // Handle different types of upload errors
        if (uploadError.message && uploadError.message.includes('collection')) {
          // Collection-related error - still show success but with warning
          emit('upload-success', {
            count: uploadParams.uploadedCount,
            collectionName: 'All Assets',
            partialSuccess: true,
            errorMessage: 'Content uploaded successfully but collection operation failed.'
          });
        } else {
          // Complete upload failure
          await handleGlobalUploadError(uploadError);
        }
      }
    }

    // Update global progress with real content IDs after successful uploads
    async function updateGlobalProgressWithRealIds(contentIds) {
      for (let i = 0; i < currentGlobalUploads.value.length && i < contentIds.length; i++) {
        const upload = currentGlobalUploads.value[i];
        const contentId = contentIds[i];

        await store.dispatch('uploadProgress/updateUploadWithRealId', {
          tempId: upload.id,
          realId: contentId,
          data: {
            progress: 30, // Initial progress after upload completion
            status: 'processing',
            message: 'Processing...'
          }
        });
      }
    }

    // Handle global upload error
    async function handleGlobalUploadError(error) {
      if (currentGlobalUploads.value.length > 0) {
        for (const upload of currentGlobalUploads.value) {
          await store.dispatch('uploadProgress/updateUpload', {
            id: upload.id,
            data: {
              status: 'error',
              progress: 100,
              message: error.message || 'Upload failed'
            }
          });
        }
      }
    }

    // Store the upload parameters
    function getUploadParameters() {
      return {
        uploadedCount: selectedFiles.value.length,
        uploadToNewCollection: uploadDestination.value === 'new',
        newCollectionNameToCreate: uploadDestination.value === 'new' ? newCollectionName.value : null,
        existingCollectionId: uploadDestination.value === 'existing' ? selectedCollection.value.value : null,
        existingCollectionName: uploadDestination.value === 'existing' ? selectedCollection.value.label : null
      };
    }

    // Optimized background file preparation - reduced processing overhead with batching
    async function prepareFileDataBackground(fileList) {
      // Process files in smaller batches to avoid blocking the UI
      const batchSize = 3;
      const results = [];

      for (let i = 0; i < fileList.length; i += batchSize) {
        const batch = fileList.slice(i, i + batchSize);

        const batchResults = await Promise.all(
          batch.map(async file => {
            // Fast extension-based detection first
            const fileName = file.name.toLowerCase();
            const isVideo = fileName.endsWith('.mp4') || fileName.endsWith('.mov') || fileName.endsWith('.mpeg') || file.type.startsWith('video/');

            // Skip metadata fetching for performance if we can infer basic info
            let metadata;
            try {
              // Only get metadata if really needed, with timeout
              const metadataPromise = fileUpload.getMediaMetadata(file);
              const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('Metadata timeout')), 2000));

              metadata = await Promise.race([metadataPromise, timeoutPromise]);
            } catch (error) {
              // Fast fallback - use file info if available
              metadata = {
                width: 1920,
                height: 1080,
                duration: isVideo ? 30 : 0 // Default 30s for videos
              };
            }

            // Optimized content type detection
            let contentType = file.type;
            if (!contentType || contentType === 'application/octet-stream') {
              if (fileName.endsWith('.mp4')) contentType = 'video/mp4';
              else if (fileName.endsWith('.mov')) contentType = 'video/quicktime';
              else if (fileName.endsWith('.jpeg') || fileName.endsWith('.jpg')) contentType = 'image/jpeg';
              else contentType = 'image/jpeg'; // Safe fallback
            }

            return {
              name: file.name,
              relativePath: file.name,
              contentType,
              isVideo,
              aspectRatio: metadata.width && metadata.height ? parseFloat((metadata.width / metadata.height).toFixed(3)) : isVideo ? 1.78 : 1.33,
              duration: isVideo ? metadata.duration || 30 : 0,
              file
            };
          })
        );

        results.push(...batchResults);

        // Yield control back to the browser between batches
        if (i + batchSize < fileList.length) {
          await new Promise(resolve => setTimeout(resolve, 0));
        }
      }

      return results;
    }

    // Get presigned URLs for upload
    async function getPresignedUrls(fileDataArray) {
      try {
        const shootId = 0; // Non-shoot-related content as specified

        const response = await ContentApi.getPresignedUrls({
          id: shootId,
          files: fileDataArray.map(fileData => ({
            name: fileData.name,
            relativePath: fileData.relativePath,
            contentType: fileData.contentType,
            aspectRatio: fileData.aspectRatio,
            duration: fileData.duration
          }))
        });

        return response.data;
      } catch (error) {
        if (error.message.includes('network') || !navigator.onLine) {
          throw new Error('Unable to prepare for upload. Please check your internet connection and try again.');
        }
        throw error;
      }
    }

    // Upload files using presigned URLs
    async function uploadFilesToStorage(fileDataArray, presignedUrls) {
      try {
        return Promise.all(
          presignedUrls.map((urlData, index) => {
            const fileData = fileDataArray[index];
            return uploadSingleFile(urlData.presignedUrl, fileData, index, presignedUrls.length);
          })
        );
      } catch (error) {
        throw new Error('An error occurred while uploading your contents. Please try again later.');
      }
    }

    // Upload a single file
    function uploadSingleFile(presignedUrl, fileData) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', presignedUrl, true);
        xhr.setRequestHeader('Content-Type', fileData.contentType);

        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve();
          } else {
            reject(new Error(`Upload failed: ${xhr.status}`));
          }
        };

        xhr.onerror = () => {
          reject(new Error('Network error during upload. Please check your connection and try again.'));
        };

        xhr.send(fileData.file);
      });
    }

    // Create content
    async function createContent(contentMetadata) {
      try {
        const shootId = 0;

        const batchResponse = await ContentApi.addContent({
          id: shootId || 0,
          payload: {
            content: contentMetadata
          }
        });

        // Get the content information from the batch response
        const contentData = batchResponse.data.content;
        const contentIds = contentData.map(content => content.id);

        if (!contentIds || contentIds.length === 0) {
          throw new Error('No content were created. The server may be experiencing issues.');
        }

        // Store the content metadata for type identification later
        contentData.forEach((content, index) => {
          // Make sure we have a valid index in contentMetadata array
          if (index < contentMetadata.length) {
            const fileData = contentMetadata[index];

            // Detect video files more robustly
            let isVideo = false;

            // First check our explicit isVideo flag if available
            if (typeof fileData.isVideo === 'boolean') {
              isVideo = fileData.isVideo;
            }
            // Next check content type
            else if (fileData.contentType?.startsWith('video/')) {
              isVideo = true;
            }
            // Finally check the filename if available
            else if (fileData.name) {
              const fileName = fileData.name.toLowerCase();
              isVideo = fileName.endsWith('.mp4') || fileName.endsWith('.mov') || fileName.endsWith('.mpeg');
            }
            // Check the server response for clues
            else if (content.mimetype && content.mimetype.startsWith('video/')) {
              isVideo = true;
            } else if (content.filename) {
              const fileName = content.filename.toLowerCase();
              isVideo = fileName.endsWith('.mp4') || fileName.endsWith('.mov') || fileName.endsWith('.mpeg');
            }

            const contentType = isVideo ? 'video' : 'image';
            const fileName = fileData.name || content.filename || `File-${index}`;

            // Associate the content ID with file info for type detection later
            uploadedContentTypes.value[content.id] = {
              type: contentType,
              duration: fileData.duration || 0,
              filename: fileName
            };
          } else {
            // Handle case where server returns more content items than we sent
            const contentMimetype = content.mimetype || '';
            let isVideo = contentMimetype.startsWith('video/');

            if (content.filename) {
              const fileName = content.filename.toLowerCase();
              isVideo = isVideo || fileName.endsWith('.mp4') || fileName.endsWith('.mov') || fileName.endsWith('.mpeg');
            }

            uploadedContentTypes.value[content.id] = {
              type: isVideo ? 'video' : 'image',
              duration: 0,
              filename: content.filename || `Unknown-${index}`
            };
          }
        });

        return contentIds;
      } catch (error) {
        if (error.message.includes('No content were created')) {
          throw new Error('The server was unable to process your content. Please try again later or contact support if the issue persists.');
        }
        throw error;
      }
    }

    // Handle collection operations
    async function handleCollectionOperations(uploadParams, contentIds) {
      let targetCollectionId = null;
      let collectionName = 'All Assets';

      if (uploadParams.uploadToNewCollection) {
        const result = await createNewCollection(uploadParams.newCollectionNameToCreate);
        targetCollectionId = result.collectionId;
        collectionName = result.collectionName;
      } else if (uploadParams.existingCollectionId) {
        // Use existing collection
        targetCollectionId = uploadParams.existingCollectionId;
        collectionName = uploadParams.existingCollectionName;
      }

      // Add to collection if needed
      if (targetCollectionId) {
        await addContentToCollection(targetCollectionId, contentIds);
      }

      return { collectionName };
    }

    // Create a new collection
    async function createNewCollection(collectionName) {
      try {
        // Double-check for duplicate collection name before creating
        if (isDuplicateCollectionName(collectionName)) {
          throw new Error('A collection with this name already exists. Please choose a different name.');
        }

        const createResponse = await CollectionsApi.createCollection(collectionName);
        return {
          collectionId: createResponse.data.collection.id,
          collectionName
        };
      } catch (collectionError) {
        if (collectionError.message.includes('already exists')) {
          // Don't emit success for collection errors - let the main success handler deal with it
          console.warn('Collection already exists:', collectionError.message);
        } else {
          // Don't emit success for collection errors - let the main success handler deal with it
          console.warn('Collection creation failed:', collectionError.message);
        }
        // content was uploaded, but collection creation failed
        throw collectionError;
      }
    }

    // Add content to a collection
    async function addContentToCollection(collectionId, contentIds) {
      try {
        // Detect if this is a video upload session
        const anyVideoFiles = selectedFiles.value.some(file => {
          // Check file extension for MP4, MOV, etc.
          const fileName = file.file.name.toLowerCase();
          const hasVideoExt = fileName.endsWith('.mp4') || fileName.endsWith('.mov');
          // Check MIME type
          const hasVideoMime = file.file.type.startsWith('video/');
          return hasVideoExt || hasVideoMime;
        });

        // Format content items with type information
        const contentItems = contentIds.map(id => {
          // Get content type from our stored mapping
          const contentInfo = uploadedContentTypes.value[id] || { type: 'image' };

          // If this is a video upload session and type detection has been unreliable,
          // ensure we use video type
          const finalType = anyVideoFiles ? 'video' : contentInfo.type;

          return {
            id: id,
            type: finalType
          };
        });

        await CollectionsApi.addItemsToCollection(collectionId, contentItems);
      } catch (addError) {
        // Don't emit success for collection errors - let the main success handler deal with it
        console.warn('Add to collection failed:', addError.message);
        // Images were uploaded, but adding to collection failed
        throw addError;
      }
    }

    // Reset form state - optimized for performance
    const resetForm = () => {
      // Only clean up blob URLs that we created locally (not from store)
      selectedFiles.value.forEach(file => {
        if (file.preview && file.preview.startsWith('blob:') && !droppedFiles.value.includes(file)) {
          URL.revokeObjectURL(file.preview);
        }
      });

      // Reset all form state efficiently
      selectedFiles.value.length = 0; // More efficient than assignment
      uploadDestination.value = 'all';
      selectedCollection.value = null;
      newCollectionName.value = '';

      // Use the store action to properly close upload dialog and re-enable drag and drop
      store.dispatch('dragUpload/closeUploadDialog');
    };

    // Clean up on dialog close
    watch(
      () => props.show,
      (isVisible, wasVisible) => {
        if (!isVisible && wasVisible) {
          // Only reset when dialog is actually closing (was visible, now not visible)
          resetForm();
        }
      }
    );

    return {
      dialogModel,
      selectedFiles,
      uploadDestination,
      selectedCollection,
      newCollectionName,
      isLoadingCollections,
      collections,
      collectionsOptions,
      canUpload,
      handleAddFiles,
      removeFile,
      uploadContent,
      handleDestinationUpdate,
      handleUploadClick,
      isDuplicateCollectionName,
      droppedFiles,
      uploadSource
    };
  }
};
</script>

<style lang="scss" scoped>
.no-shadow-dialog {
  :deep(.q-dialog__backdrop) {
    background: rgba(0, 0, 0, 0.4) !important;
  }

  :deep(.q-card) {
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15) !important;
  }
}

.upload-dialog-card {
  min-width: 600px;
  max-width: 90vw;
  max-height: 90vh; /* Limit height to 90% of viewport */
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
}

.upload-dialog-header {
  padding: 24px 24px;
  background: linear-gradient(to right, #f7f7f7, #ffffff);
  border-radius: 16px 16px 0 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0; /* Prevent header from shrinking */
}

.header-content {
  display: flex;
  flex-direction: column;
}

.upload-dialog-title {
  @include mona-sans-font($size: 20px, $bolded: true, $color: var(--main-text-color));
  letter-spacing: 0.3px;
  margin: 0;
  line-height: 1.2;
}

.upload-dialog-subtitle {
  @include inter-font($size: 14px, $color: var(--secondary-text-color));
  margin-top: 4px;
  margin-bottom: 0;
}

.upload-dialog-body {
  padding: 24px;
  position: relative;
  transition: filter 0.3s ease;
  overflow-y: auto; /* Enable vertical scrolling */
  flex: 1 1 auto; /* Allow body to grow and shrink */
  max-height: calc(90vh - 140px); /* Subtract header and footer heights */
}

.scroll-container {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

.scroll-container::-webkit-scrollbar {
  width: 6px;
}

.scroll-container::-webkit-scrollbar-track {
  background: transparent;
}

.scroll-container::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.upload-dialog-actions {
  padding: 16px 24px 24px;
  background-color: white;
  border-top: 1px solid rgba(0, 0, 0, 0.03);
  flex-shrink: 0; /* Prevent footer from shrinking */
}

.close-btn {
  opacity: 0.7;
  transition: opacity 0.2s ease, background-color 0.2s ease;
}

.close-btn:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.05);
}

.cancel-btn,
.upload-btn {
  @include inter-font($size: 14px, $bolded: true, $color: #333);
  height: 44px;
  border-radius: 8px;
  letter-spacing: 0.3px;
  padding: 0 20px;
  transition: all 0.2s ease;
}

.cancel-btn {
  margin-right: 12px;
}

.cancel-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.upload-btn {
  background-color: var(--main-btn-color);
  color: var(--main-text-color);
}

.upload-btn:hover {
  opacity: 0.9;
}
</style>
